import React from 'react';
import { Edit, FileSearch, Trash2, Undo } from 'lucide-react';
import {
  type PolicyWithChange,
  getPolicyState,
} from '../utils/policyProposalUtils';

interface MenuItemProps {
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

interface PolicyMenuOptionsProps {
  policy: PolicyWithChange;
  onEditPolicy?: ((policy: PolicyWithChange) => void) | undefined;
  onReviewChanges?: ((policy: PolicyWithChange) => void) | undefined;
  onRemovePolicy?: ((policy: PolicyWithChange) => void) | undefined;
  onUndoProposal?: ((policy: PolicyWithChange) => void) | undefined;
  onActionComplete?: (() => void) | undefined; // Callback when any action is completed
  disableEditAndRemove?: boolean; // Whether edit and remove options should be disabled (e.g., in review mode)
  // Generic menu item components that can be DropdownMenuItem or ContextMenuItem
  MenuItem: React.ComponentType<MenuItemProps>;
  MenuSeparator: React.ComponentType<Record<string, never>>;
}

export function PolicyMenuOptions({
  policy,
  onEditPolicy,
  onReviewChanges,
  onRemovePolicy,
  onUndoProposal,
  onActionComplete,
  disableEditAndRemove = false,
  MenuItem,
  MenuSeparator,
}: PolicyMenuOptionsProps) {
  const handleMenuAction = (action: string, e: React.MouseEvent) => {
    e.stopPropagation();

    switch (action) {
      case 'edit':
        onEditPolicy?.(policy);
        break;
      case 'review':
        onReviewChanges?.(policy);
        break;
      case 'remove':
        onRemovePolicy?.(policy);
        break;
      case 'undo':
        onUndoProposal?.(policy);
        break;
    }

    // Call the completion callback to close menus
    onActionComplete?.();
  };

  const policyState = getPolicyState(policy);
  const canReviewChanges = policyState === 'modified';
  const hasChanges = policyState !== 'original'; // new, modified, or deleted
  const canUndo = hasChanges && !!onUndoProposal;
  const canEdit = !disableEditAndRemove;

  // Don't render any menu items if there are no available actions
  const hasAnyOption =
    (canReviewChanges && onReviewChanges) || canUndo || canEdit;
  if (!hasAnyOption) {
    return null;
  }

  return (
    <>
      {/* Review changes - show for modified policies if handler provided */}
      {canReviewChanges && onReviewChanges && (
        <MenuItem
          onClick={(e: React.MouseEvent) => {
            handleMenuAction('review', e);
          }}
        >
          <FileSearch className="mr-2 h-4 w-4" />
          Review changes
        </MenuItem>
      )}

      {/* Separator between review and undo/edit */}
      {canReviewChanges && onReviewChanges && (canUndo || canEdit) && (
        <MenuSeparator />
      )}

      {/* Undo changes - show only if handler provided AND policy has changes */}
      {canUndo && (
        <MenuItem
          onClick={(e: React.MouseEvent) => {
            handleMenuAction('undo', e);
          }}
        >
          <Undo className="mr-2 h-4 w-4" />
          Undo changes
        </MenuItem>
      )}

      {/* Edit/Remove - show only if no changes AND not disabled */}
      {canEdit && (
        <>
          <MenuItem
            onClick={(e: React.MouseEvent) => {
              handleMenuAction('edit', e);
            }}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit policy
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            onClick={(e: React.MouseEvent) => {
              handleMenuAction('remove', e);
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Remove policy
          </MenuItem>
        </>
      )}
    </>
  );
}
